import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store/store";
import {
  setExportHistoryData,
  setLoading,
  setError,
  setPagination,
  setSearchQuery,
  setSortBy,
  exportHistorySelector,
} from "../store/features/exportHistorySlice";
import { useExportHistory } from "@/services/payment.services";
import { PaymentPagination, PaymentSortBy } from "@/types/payment";

export const useExportHistoryHook = (disableApiCalls: boolean = false) => {
  const dispatch = useDispatch<AppDispatch>();
  const exportHistoryState = useSelector(exportHistorySelector);
  const { 
    pagination = { pageSize: 25, pageNumber: 1, page: 1, per_page: 25, total: 0, total_pages: 0 },
    searchQuery = "",
    sortBy = []
  } = (exportHistoryState as any) || {};

  // Prepare API parameters
  const currentDate = new Date();
  const defaultFromDate = new Date();
  defaultFromDate.setDate(currentDate.getDate() - 30);
  
  const defaultToDate = new Date();
  defaultToDate.setDate(currentDate.getDate() + 1);
  
  const apiParams = {
    page: pagination.pageNumber,
    per_page: pagination.pageSize,
    search: searchQuery,
    sortBy,
    from_date: defaultFromDate.toISOString().split('T')[0],
    to_date: defaultToDate.toISOString().split('T')[0],
  };

  // Fetch export history data
  const {
    data: exportHistoryResponse,
    isLoading: exportHistoryLoading,
    error: exportHistoryError,
    refetch: refetchExportHistory,
  } = useExportHistory({
    ...apiParams,
    enabled: !disableApiCalls,
  });

  // Update Redux state when data changes
  useEffect(() => {
    if (exportHistoryResponse) {
      // Handle different response formats from backend
      const exportHistoryData = exportHistoryResponse.data || exportHistoryResponse || [];
      const processedData = Array.isArray(exportHistoryData) ? exportHistoryData : [];
      dispatch(setExportHistoryData(processedData));
    }
  }, [exportHistoryResponse, dispatch]);

  // Auto-refresh data when sortBy changes
  useEffect(() => {
    if (sortBy && sortBy.length > 0) {
      refetchExportHistory();
    }
  }, [sortBy, refetchExportHistory]);

  useEffect(() => {
    const isLoading = exportHistoryLoading;
    dispatch(setLoading(isLoading));
  }, [exportHistoryLoading, dispatch]);

  useEffect(() => {
    const error = exportHistoryError;
    dispatch(setError(error?.message || null));
  }, [exportHistoryError, dispatch]);

  // Clear data when API calls are disabled
  useEffect(() => {
    if (disableApiCalls) {
      dispatch(setExportHistoryData(null));
      dispatch(setError(null));
    }
  }, [disableApiCalls, dispatch]);

  // Helper functions
  const refreshData = async () => {
    try {
      await refetchExportHistory();
    } catch (error) {
      // Dispatch error to Redux state
      dispatch(setError(error instanceof Error ? error.message : 'Failed to refresh data'));
    }
  };

  return {
    ...(exportHistoryState || {}),
    refreshData,
    totalPages: exportHistoryResponse?.totalPages || 0,
    totalCount: exportHistoryResponse?.pagination?.total || 0,
    setSearchQuery: (query: string) => dispatch(setSearchQuery(query)),
    setPagination: (pagination: PaymentPagination) => dispatch(setPagination(pagination)),
    setSortBy: (sortBy: PaymentSortBy[]) => dispatch(setSortBy(sortBy)),
  };
};
